import { useState } from "react";
import { format } from "date-fns";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DATE_LOCALES, useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { iso } from "@/lib/booking";
import { Breakdown } from "./BookingWidget";
import { PayPalPay } from "./PayPalPay";

export const RequestDialog = ({ open, onOpenChange, prop, b, onBooked }) => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const [paid, setPaid] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const f = (d) => (d ? format(d, "EEE d MMM yyyy", { locale: DATE_LOCALES[lang] }) : "");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/properties/${prop.slug}/bookings`, {
        check_in: iso(b.range.from), check_out: iso(b.range.to), adults: b.adults, children: b.children,
        name: form.name, email: form.email, phone: form.phone || null, message: form.message || null, lang,
      });
      setDone(data);
      onBooked?.();
    } catch (ex) {
      setErr(errMsg(ex, t("f.err")));
    } finally {
      setBusy(false);
    }
  };
  const close = (v) => {
    onOpenChange(v);
    if (!v && done) { setDone(null); setPaid(false); b.clear(); setForm({ name: "", email: "", phone: "", message: "" }); }
  };
  const wa = prop?.contact?.whatsapp;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="booking-request-dialog" className="max-h-[92dvh] overflow-y-auto rounded-3xl border-0 bg-paper p-6 sm:max-w-lg sm:p-8" data-lenis-prevent>
        {done ? (
          <div className="py-4 text-center" data-testid="booking-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-forest" strokeWidth={1.3} />
            <DialogTitle className="mt-4 font-display text-4xl font-medium text-ink">{t("f.okTitle")}</DialogTitle>
            <p className="mx-auto mt-3 max-w-sm text-[15px] text-muted2">{t("f.okBody")}</p>
            <div className="mx-auto mt-6 inline-block rounded-2xl border border-dashed border-accent2 px-6 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brown">{t("f.code")}</p>
              <p data-testid="booking-code" className="font-mono text-xl font-semibold tracking-widest text-ink">{done.code}</p>
            </div>
            {paid ? <p data-testid="payment-success" className="mt-6 rounded-2xl bg-forest/10 p-4 text-sm font-semibold text-forest">{t("pay.done")}</p> : <PayPalPay booking={done} onPaid={() => setPaid(true)} />}
            <div className="mt-6 flex flex-col gap-3">
              {wa && <a data-testid="booking-whatsapp-link" href={`https://wa.me/${wa.replace(/\D/g, "")}?text=${encodeURIComponent(`${done.code} · ${done.check_in} → ${done.check_out}`)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-forest py-3 text-sm font-semibold text-white"><MessageCircle className="h-4 w-4" />{t("f.wa")}</a>}
              <button data-testid="booking-success-close" onClick={() => close(false)} className="rounded-2xl border border-ink/20 py-3 text-sm font-semibold">{t("f.close")}</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <DialogTitle className="font-display text-3xl font-medium text-ink">{t("f.title")}</DialogTitle>
            <div className="rounded-2xl bg-white p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brown">{t("f.trip")}</p>
              <p className="mt-2 text-sm text-ink">{f(b.range.from)} → {f(b.range.to)}</p>
              <p className="text-sm text-muted2">{b.adults + b.children} {b.adults + b.children === 1 ? t("l.guest") : t("l.guests")}</p>
              <Breakdown b={b} />
            </div>
            <Input data-testid="booking-name-input" required minLength={2} placeholder={t("f.name")} value={form.name} onChange={set("name")} className="h-12 rounded-xl bg-white" />
            <Input data-testid="booking-email-input" required type="email" placeholder={t("f.email")} value={form.email} onChange={set("email")} className="h-12 rounded-xl bg-white" />
            <Input data-testid="booking-phone-input" placeholder={t("f.phone")} value={form.phone} onChange={set("phone")} className="h-12 rounded-xl bg-white" />
            <Textarea data-testid="booking-message-input" placeholder={t("f.message")} value={form.message} onChange={set("message")} className="min-h-[96px] rounded-xl bg-white" />
            {err && <p data-testid="booking-error" className="rounded-xl bg-cta/10 p-3 text-sm text-[#8f3c20]">{err}</p>}
            <button data-testid="booking-submit-button" disabled={busy} className="w-full rounded-2xl bg-cta py-4 font-semibold text-paper transition-colors hover:bg-cta/85 disabled:opacity-60">
              {busy ? t("f.sending") : t("f.send")}
            </button>
            <p className="text-center text-xs text-muted2">{t("w.noCharge")}</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
